/**
 * Alt Yüklenici — Maaş Ödemeleri
 * ================================
 * Manuel giriş + Excel içe aktarım. Ekip cari & proje toplamları özet kartları.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Plus, PencilSimple, TrashSimple, MagnifyingGlass, HandCoins, UsersThree,
  Buildings, ArrowLeft, FileXls, DownloadSimple, UploadSimple, CurrencyCircleDollar,
  CaretUp, CaretDown, CaretUpDown, FilePdf,
} from "@phosphor-icons/react";

// ---- Turkish money formatting ----
const formatTR = (s) => {
  if (s === "" || s == null) return "";
  const n = Number(String(s).replace(/\./g, "").replace(",", "."));
  if (!isFinite(n)) return "";
  const [intPart, decPart] = String(n).split(".");
  const withDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decPart ? `${withDots},${decPart}` : withDots;
};
const parseTR = (s) => String(s || "").replace(/\./g, "").replace(",", ".");
function MoneyInput({ value, onChange, "data-testid": testId, ...rest }) {
  const [display, setDisplay] = useState(() => formatTR(value));
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setDisplay(formatTR(value));
  }, [value]);
  return (
    <Input type="text" inputMode="decimal" value={display}
           onFocus={() => { focusedRef.current = true; }}
           onBlur={() => { focusedRef.current = false; setDisplay(formatTR(value)); }}
           onChange={(e) => {
             const raw = e.target.value.replace(/[^\d,.]/g, "");
             const canonical = parseTR(raw);
             const intPart = canonical.split(".")[0] || "";
             const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
             const decMatch = raw.match(/,(\d*)$/);
             const out = raw.endsWith(",") ? intFormatted + ","
               : decMatch ? intFormatted + "," + decMatch[1] : intFormatted;
             setDisplay(out);
             onChange(canonical);
           }}
           data-testid={testId} {...rest} />
  );
}

const fmtTL = (n) => new Intl.NumberFormat("tr-TR", {
  style: "currency", currency: "TRY", maximumFractionDigits: 2,
}).format(Number(n) || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  id: null, site_id: "", team_id: "", employee_name: "",
  pay_date: todayISO(), amount: "", account_info: "", notes: "",
};

export default function SubSalaryPayments() {
  const { sites, activeSiteId } = useSites();
  const confirm = useConfirm();

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ grand_total: 0, total_count: 0, by_team: [], by_site: [] });
  const [teamsAll, setTeamsAll] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [fSite, setFSite] = useState(activeSiteId || "all");
  const [fTeam, setFTeam] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [q, setQ] = useState("");
  // Sort: field + direction ('asc' | 'desc')
  const [sort, setSort] = useState({ field: "pay_date", dir: "desc" });

  const toggleSort = (field) => {
    setSort((s) => s.field === field
      ? { field, dir: s.dir === "asc" ? "desc" : "asc" }
      : { field, dir: "asc" });
  };
  const SortIcon = ({ field }) => {
    if (sort.field !== field) {
      return <CaretUpDown size={11} className="inline text-slate-300 ml-1" />;
    }
    return sort.dir === "asc"
      ? <CaretUp size={11} weight="bold" className="inline text-[#1B4776] ml-1" />
      : <CaretDown size={11} weight="bold" className="inline text-[#1B4776] ml-1" />;
  };

  // manual add dialog
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // import dialog
  const [impOpen, setImpOpen] = useState(false);
  const [impSite, setImpSite] = useState(activeSiteId || "");
  const [impTeam, setImpTeam] = useState("");
  const [impFile, setImpFile] = useState(null);
  const [impBusy, setImpBusy] = useState(false);

  useEffect(() => {
    setFSite(activeSiteId || "all");
    if (activeSiteId) setImpSite(activeSiteId);
  }, [activeSiteId]);

  // Load all teams (single fetch, filtered client-side per site)
  const loadTeams = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/teams`);
      setTeamsAll(Array.isArray(data) ? data : (data.items || []));
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fSite !== "all") params.site_id = fSite;
      if (fTeam !== "all") params.team_id = fTeam;
      if (fFrom) params.date_from = fFrom;
      if (fTo) params.date_to = fTo;
      const [r, s] = await Promise.all([
        axios.get(`${API}/salary-payments`, { params }),
        axios.get(`${API}/salary-payments/summary`, {
          params: { site_id: params.site_id, date_from: params.date_from, date_to: params.date_to },
        }),
      ]);
      setRows(Array.isArray(r.data) ? r.data : []);
      setSummary(s.data || { grand_total: 0, total_count: 0, by_team: [], by_site: [] });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [fSite, fTeam, fFrom, fTo]);

  useEffect(() => { loadTeams(); }, [loadTeams]);
  useEffect(() => { loadRows(); }, [loadRows]);

  // Teams: yalnızca seçilen şantiyeye ait ekipler (site_id === siteId)
  // Şantiyesiz "global" ekipler artık dahil edilmez — Şubat 2026 sonrası
  // her ekip mutlaka bir şantiyeye bağlı oluşturulur.
  const teamsForSite = (siteId) => {
    if (!siteId || siteId === "all") return teamsAll;
    return teamsAll.filter((t) => t.site_id === siteId);
  };

  const filteredRows = useMemo(() => {
    const term = q.trim().toLocaleLowerCase("tr");
    const base = !term ? rows : rows.filter((r) =>
      [r.employee_name, r.team_name, r.site_name, r.account_info, r.notes]
        .filter(Boolean)
        .some((s) => String(s).toLocaleLowerCase("tr").includes(term)));
    // Sıralama
    const cmp = (a, b) => {
      const av = a[sort.field]; const bv = b[sort.field];
      if (sort.field === "amount") {
        return (Number(av) || 0) - (Number(bv) || 0);
      }
      // Tarih ISO (YYYY-MM-DD) — lexicographic sort doğru sonucu verir
      return String(av || "").localeCompare(String(bv || ""), "tr", { sensitivity: "base" });
    };
    const sorted = [...base].sort(cmp);
    return sort.dir === "desc" ? sorted.reverse() : sorted;
  }, [rows, q, sort]);

  // ---- Manual add ----
  const openNew = () => {
    setForm({
      ...emptyForm,
      site_id: fSite !== "all" ? fSite : (activeSiteId || ""),
      team_id: fTeam !== "all" ? fTeam : "",
    });
    setDlgOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      id: r.id, site_id: r.site_id, team_id: r.team_id,
      employee_name: r.employee_name, pay_date: r.pay_date,
      amount: String(r.amount || ""), account_info: r.account_info || "",
      notes: r.notes || "",
    });
    setDlgOpen(true);
  };
  const save = async () => {
    if (!form.site_id) { toast.error("Şantiye seçin"); return; }
    if (!form.team_id) { toast.error("Ekip seçin"); return; }
    if (!form.employee_name.trim()) { toast.error("Personel adı zorunlu"); return; }
    if (!form.pay_date) { toast.error("Ödeme tarihi zorunlu"); return; }
    const amt = parseFloat(form.amount);
    if (!(amt > 0)) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    try {
      const payload = {
        site_id: form.site_id, team_id: form.team_id,
        employee_name: form.employee_name.trim(),
        pay_date: form.pay_date,
        amount: amt,
        account_info: form.account_info.trim(),
        notes: form.notes.trim(),
      };
      if (form.id) {
        await axios.put(`${API}/salary-payments/${form.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await axios.post(`${API}/salary-payments`, payload);
        toast.success("Ödeme kaydedildi");
      }
      setDlgOpen(false);
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (r) => {
    const ok = await confirm({
      title: "Ödemeyi Sil",
      description: `${r.employee_name} - ${fmtTL(r.amount)} kaydını silmek istiyor musunuz?`,
      destructive: true, confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/salary-payments/${r.id}`);
      toast.success("Silindi");
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---- Excel import ----
  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/salary-payments/template.xlsx`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "maas_odeme_sablonu.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const doImport = async () => {
    if (!impSite) { toast.error("Şantiye seçin"); return; }
    if (!impTeam) { toast.error("Ekip seçin"); return; }
    if (!impFile) { toast.error("Excel dosyası seçin"); return; }
    setImpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", impFile);
      const { data } = await axios.post(
        `${API}/salary-payments/import?site_id=${impSite}&team_id=${impTeam}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${data.inserted} kayıt aktarıldı${data.errors?.length ? ` (${data.errors.length} hatalı satır)` : ""}`);
      if (data.errors?.length) {
        console.warn("Import errors:", data.errors);
      }
      setImpOpen(false);
      setImpFile(null);
      loadRows();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setImpBusy(false); }
  };

  // ---- Export (uygulanan filtrelerle) ----
  const buildExportParams = () => {
    const p = new URLSearchParams();
    if (fSite !== "all") p.set("site_id", fSite);
    if (fTeam !== "all") p.set("team_id", fTeam);
    if (fFrom) p.set("date_from", fFrom);
    if (fTo) p.set("date_to", fTo);
    if (q.trim()) p.set("q", q.trim());
    return p.toString();
  };
  const exportXlsx = async () => {
    try {
      const res = await axios.get(`${API}/salary-payments/export.xlsx?${buildExportParams()}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "maas_odemeleri.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const exportPdf = async () => {
    try {
      const res = await axios.get(`${API}/salary-payments/report.pdf?${buildExportParams()}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      // URL 60s sonra revoke — açık sekmede sorun olmasın
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const totalAmt = summary.grand_total || 0;

  return (
    <Shell
      title="Maaş Ödemeleri"
      subtitle="Alt Yüklenici Yönetimi › Ödemeler"
      actions={
        <Link to="/taseron/odemeler">
          <Button variant="outline" data-testid="salary-back"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            <ArrowLeft size={14} className="mr-1.5" /> Ödeme Tipleri
          </Button>
        </Link>
      }
    >
      <div className="space-y-6" data-testid="salary-payments-page">
        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card className="p-4 rounded-none sm:rounded-sm border-l-4 border-[#95C11F]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Toplam Ödeme
            </div>
            <div className="text-2xl font-bold text-[#1B4776] mt-1 font-heading" data-testid="stat-total">
              {fmtTL(totalAmt)}
            </div>
          </Card>
          <Card className="p-4 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Ödeme Sayısı
            </div>
            <div className="text-3xl font-bold text-[#1B4776] mt-1 font-heading">{summary.total_count}</div>
          </Card>
          <Card className="p-4 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Ekip Sayısı
            </div>
            <div className="text-3xl font-bold text-[#95C11F] mt-1 font-heading">{summary.by_team.length}</div>
          </Card>
          <Card className="p-4 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Şantiye Sayısı
            </div>
            <div className="text-3xl font-bold text-[#95C11F] mt-1 font-heading">{summary.by_site.length}</div>
          </Card>
        </div>

        {/* Actions & Filters */}
        <Card className="p-4 rounded-none sm:rounded-sm">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ara</Label>
              <div className="relative mt-1">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Personel / hesap / not"
                       data-testid="salary-search"
                       className="pl-8 rounded-none sm:rounded-sm h-9" />
              </div>
            </div>
            <div className="min-w-[180px]">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Şantiye</Label>
              <Select value={fSite} onValueChange={(v) => { setFSite(v); setFTeam("all"); }}>
                <SelectTrigger className="rounded-none sm:rounded-sm h-9 mt-1" data-testid="filter-site">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Şantiyeler</SelectItem>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ekip</Label>
              <Select value={fTeam} onValueChange={setFTeam}>
                <SelectTrigger className="rounded-none sm:rounded-sm h-9 mt-1" data-testid="filter-team">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Ekipler</SelectItem>
                  {teamsForSite(fSite).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Başlangıç</Label>
              <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
                     className="rounded-none sm:rounded-sm h-9 mt-1" data-testid="filter-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Bitiş</Label>
              <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)}
                     className="rounded-none sm:rounded-sm h-9 mt-1" data-testid="filter-to" />
            </div>
            <div className="flex gap-2">
              <Button onClick={exportXlsx} data-testid="btn-export-xlsx"
                      variant="outline"
                      className="rounded-none sm:rounded-sm h-9">
                <FileXls size={14} className="mr-1.5 text-emerald-700" />
                <span className="text-xs uppercase tracking-widest font-bold">Excel</span>
              </Button>
              <Button onClick={exportPdf} data-testid="btn-export-pdf"
                      variant="outline"
                      className="rounded-none sm:rounded-sm h-9">
                <FilePdf size={14} className="mr-1.5 text-red-600" />
                <span className="text-xs uppercase tracking-widest font-bold">PDF</span>
              </Button>
              <Button onClick={() => setImpOpen(true)} data-testid="btn-import"
                      variant="outline"
                      className="rounded-none sm:rounded-sm h-9">
                <UploadSimple size={14} className="mr-1.5 text-slate-600" />
                <span className="text-xs uppercase tracking-widest font-bold">İçe Aktar</span>
              </Button>
              <Button onClick={openNew} data-testid="btn-new"
                      className="rounded-none sm:rounded-sm h-9 bg-[#1B4776] hover:bg-[#153859] text-white">
                <Plus size={14} className="mr-1.5" />
                <span className="text-xs uppercase tracking-widest font-bold">Yeni Ödeme</span>
              </Button>
            </div>
          </div>
        </Card>

        {/* Team cari breakdown */}
        {summary.by_team.length > 0 && (
          <Card className="p-4 rounded-none sm:rounded-sm" data-testid="team-cari">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 mb-3 flex items-center gap-2">
              <UsersThree size={14} weight="duotone" className="text-[#95C11F]" />
              Ekip Bazlı Cari
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {summary.by_team.map((t) => (
                <div key={t.team_id || Math.random()}
                     data-testid={`team-cari-${t.team_id}`}
                     className="flex items-center justify-between border border-slate-200 px-3 py-2 rounded-sm hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">
                      {t.team_name || "—"}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">
                      {t.site_code || ""} · {t.count} ödeme
                    </div>
                  </div>
                  <div className="text-sm font-bold text-[#1B4776] font-mono ml-2">
                    {fmtTL(t.amount)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Table */}
        <Card className="rounded-none sm:rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="salary-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-slate-100"
                      onClick={() => toggleSort("pay_date")} data-testid="sort-pay_date">
                    Tarih <SortIcon field="pay_date" />
                  </th>
                  <th className="text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-slate-100"
                      onClick={() => toggleSort("site_code")} data-testid="sort-site_code">
                    Şantiye <SortIcon field="site_code" />
                  </th>
                  <th className="text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-slate-100"
                      onClick={() => toggleSort("team_name")} data-testid="sort-team_name">
                    Ekip <SortIcon field="team_name" />
                  </th>
                  <th className="text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-slate-100"
                      onClick={() => toggleSort("employee_name")} data-testid="sort-employee_name">
                    Personel <SortIcon field="employee_name" />
                  </th>
                  <th className="text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-slate-100"
                      onClick={() => toggleSort("account_info")} data-testid="sort-account_info">
                    Hesap Bilgisi <SortIcon field="account_info" />
                  </th>
                  <th className="text-right px-3 py-2 font-bold cursor-pointer select-none hover:bg-slate-100"
                      onClick={() => toggleSort("amount")} data-testid="sort-amount">
                    Tutar <SortIcon field="amount" />
                  </th>
                  <th className="text-right px-3 py-2 font-bold">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400 text-xs">Yükleniyor…</td></tr>
                ) : filteredRows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400 text-xs" data-testid="salary-empty">
                    Henüz maaş ödemesi girilmedi. Yeni Ödeme veya Excel İçe Aktar ile başlayabilirsiniz.
                  </td></tr>
                ) : filteredRows.map((r, i) => (
                  <tr key={r.id} data-testid={`salary-row-${i}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{fmtDate(r.pay_date)}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs text-slate-500 font-mono">{r.site_code}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-bold text-slate-900">{r.team_name || "—"}</div>
                      {r.team_leader && (
                        <div className="text-[10px] text-slate-500">{r.team_leader}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-bold text-slate-900">{r.employee_name}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 font-mono max-w-[220px] truncate" title={r.account_info}>
                      {r.account_info || "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-[#1B4776]">
                      {fmtTL(r.amount)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => openEdit(r)} data-testid={`edit-${i}`}
                              title="Düzenle"
                              className="w-7 h-7 inline-flex items-center justify-center text-[#1B4776] hover:bg-slate-100">
                        <PencilSimple size={13} />
                      </button>
                      <button onClick={() => del(r)} data-testid={`del-${i}`}
                              title="Sil"
                              className="w-7 h-7 inline-flex items-center justify-center text-red-600 hover:bg-red-50">
                        <TrashSimple size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Manual Dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold font-heading">
              {form.id ? "Maaş Ödemesini Düzenle" : "Yeni Maaş Ödemesi"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Personel, ekip, tarih, tutar ve hesap bilgilerini girin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Şantiye *</Label>
                <Select value={form.site_id}
                        onValueChange={(v) => setForm({ ...form, site_id: v, team_id: "" })}>
                  <SelectTrigger className="rounded-none sm:rounded-sm mt-1" data-testid="form-site">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Ekip *</Label>
                <Select value={form.team_id}
                        onValueChange={(v) => setForm({ ...form, team_id: v })}
                        disabled={!form.site_id}>
                  <SelectTrigger className="rounded-none sm:rounded-sm mt-1" data-testid="form-team">
                    <SelectValue placeholder={form.site_id ? "Seçin" : "Önce şantiye seçin"} />
                  </SelectTrigger>
                  <SelectContent>
                    {form.site_id && teamsForSite(form.site_id).length === 0 && (
                      <div className="p-2 text-[11px] text-rose-600 italic">
                        Bu şantiyede tanımlı ekip yok
                      </div>
                    )}
                    {teamsForSite(form.site_id).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Personel Adı Soyadı *</Label>
              <Input value={form.employee_name}
                     onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                     placeholder="Örn: Ahmet Yılmaz"
                     data-testid="form-name"
                     className="rounded-none sm:rounded-sm mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Ödeme Tarihi *</Label>
                <Input type="date" value={form.pay_date}
                       onChange={(e) => setForm({ ...form, pay_date: e.target.value })}
                       data-testid="form-date"
                       className="rounded-none sm:rounded-sm mt-1 font-mono" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Tutar (₺) *</Label>
                <MoneyInput value={form.amount}
                            onChange={(v) => setForm({ ...form, amount: v })}
                            placeholder="0,00"
                            data-testid="form-amount"
                            className="rounded-none sm:rounded-sm mt-1 font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Hesap Bilgileri</Label>
              <Input value={form.account_info}
                     onChange={(e) => setForm({ ...form, account_info: e.target.value })}
                     placeholder="Banka / IBAN / Hesap No"
                     data-testid="form-account"
                     className="rounded-none sm:rounded-sm mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Notlar</Label>
              <Textarea value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={2}
                        data-testid="form-notes"
                        className="rounded-none sm:rounded-sm mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={save} data-testid="form-save"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              {form.id ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold font-heading flex items-center gap-2">
              <FileXls size={18} className="text-emerald-700" />
              Excel ile Toplu Maaş Ödemesi
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Aşağıdaki şantiye + ekip seçimine bağlı olarak toplu ödeme kaydı oluşturulur.
              Şablonu indirip doldurun, ardından yükleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Button variant="outline" onClick={downloadTemplate}
                      data-testid="btn-download-template"
                      className="rounded-none sm:rounded-sm h-9 w-full">
                <DownloadSimple size={14} className="mr-1.5" />
                <span className="text-xs uppercase tracking-widest font-bold">Excel Şablonu İndir</span>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Şantiye *</Label>
                <Select value={impSite} onValueChange={(v) => { setImpSite(v); setImpTeam(""); }}>
                  <SelectTrigger className="rounded-none sm:rounded-sm mt-1" data-testid="imp-site">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Ekip *</Label>
                <Select value={impTeam} onValueChange={setImpTeam} disabled={!impSite}>
                  <SelectTrigger className="rounded-none sm:rounded-sm mt-1" data-testid="imp-team">
                    <SelectValue placeholder={impSite ? "Seçin" : "Önce şantiye seçin"} />
                  </SelectTrigger>
                  <SelectContent>
                    {teamsForSite(impSite).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Excel Dosyası (.xlsx) *</Label>
              <Input type="file" accept=".xlsx,.xlsm"
                     onChange={(e) => setImpFile(e.target.files?.[0] || null)}
                     data-testid="imp-file"
                     className="rounded-none sm:rounded-sm mt-1" />
              <div className="text-[10px] text-slate-500 mt-1">
                Kolonlar: Personel Adı Soyadı · Ekip · Ödeme Tarihi · Tutar · Hesap · Notlar
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpOpen(false)}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={doImport} disabled={impBusy} data-testid="imp-submit"
                    className="rounded-none sm:rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white">
              <UploadSimple size={14} className="mr-1.5" />
              <span className="text-xs uppercase tracking-widest font-bold">
                {impBusy ? "Yükleniyor…" : "İçe Aktar"}
              </span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
